import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Close } from "@mui/icons-material";
import useOutsideClick from "../../Hooks/useOutsideClick";
import AsyncSelect from "react-select/async";
import debounce from "debounce-promise";
import { components } from "react-select";
import backgroundImage from "~/images/modal-background-image.png";
import CheckBox from "../../Checkbox";

const ClearIndicator = (props) => {
  return (
    <div onMouseDown={(e) => e.stopPropagation()}>
      <components.ClearIndicator {...props} />
    </div>
  );
};

const customSelectStyles = {
  container: (base) => ({
    ...base,
    margin: "16px 0 0",
  }),
  control: (base) => ({
    ...base,
    height: 36,
    borderColor: "#DEE7EE",
  }),
};

const CreationModal = (props) => {
  const [checked, setChecked] = useState({
    authorization: false,
    terms: false,
  });
  const [selectedEntity, setSelectedEntity] = useState();
  const [submitting, setSubmitting] = useState(false);

  const modalRef = useRef();

  const loadOptions = (query) => fetchSearchTerm(query);
  const debouncedLoadOptions = debounce(loadOptions, 800, {
    leading: true,
  });

  const fetchSearchTerm = async (query) => {
    let url = new URL("/api/entities", window.location.origin);
    url.search = new URLSearchParams({
      query: query,
    }).toString();

    const results = await fetch(url)
      .then((response) => response.json())
      .then((data) => data)
      .catch((error) => {
        console.error("Error:", error);
      });

    return results;
  };

  useOutsideClick(modalRef, props.closeModal);

  const checkboxes = [
    {
      title: "Confirm Your Authorization",
      subtitle:
        "Are you authorized by the selected entity to enroll into 340B ESP on their behalf?",
      display: "I am authorized",
      value: "authorization",
    },
    {
      title: "Terms and Privacy Policy",
      subtitle: (
        <>
          By clicking on the box below you confirm our understanding of the{" "}
          <a
            className="t--anchor t--blue"
            style={{ textDecoration: "underline" }}
            href="/terms-of-use"
            target="_blank"
          >
            Terms of Use
          </a>{" "}
          and{" "}
          <a
            className="t--anchor t--blue"
            style={{ textDecoration: "underline" }}
            href="/privacy"
            target="_blank"
          >
            Privacy Policy
          </a>
          .
        </>
      ),
      display: "Accept Terms & Privacy Policy",
      value: "terms",
    },
  ];

  function boxesUnchecked() {
    return Object.keys(checked).some((checkbox) => !checked[checkbox]);
  }

  function entityInputEmpty() {
    return !selectedEntity;
  }

  function submitDisabled() {
    return boxesUnchecked() || entityInputEmpty() || submitting;
  }

  function handleSubmit() {
    if (submitting) {
      return;
    }
    setSubmitting(true);

    fetch(`/organization/${props.organizationID}/registrations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content"),
      },
      body: JSON.stringify({ id_340b: selectedEntity.id_340b }),
    })
      .then((response) => response.json())
      .then((data) => {
        sessionStorage.setItem(props.resultKey, JSON.stringify(data));
        window.location.reload();
      })
      .catch(() => {
        sessionStorage.setItem(
          props.resultKey,
          JSON.stringify({
            status: "error",
            error: "There was an issue submitting your New Entity Enrollment. Please try again.",
          })
        );
        window.location.reload();
      });
  }

  function handleCheck(resource) {
    setChecked({ ...checked, [resource]: !checked[resource] });
  }

  function renderCheckboxes() {
    return checkboxes.map((checkbox, index) => {
      return (
        <React.Fragment key={checkbox.value}>
          <div className="flex flex-column gap--4">
            <div className="section__title">{checkbox.title}</div>
            <div>{checkbox.subtitle}</div>
          </div>
          <div
            style={index < checkboxes.length - 1 ? { marginBottom: "8px" } : undefined}
            className="tpa-modal__check-item gap--8"
            onClick={() => handleCheck(checkbox.value)}
          >
            <CheckBox isSelected={checked[checkbox.value]} />
            <div>{checkbox.display}</div>
          </div>
        </React.Fragment>
      );
    });
  }

  return createPortal(
    <div className="tpa-modal__background">
      <div className="tpa-modal__container" ref={modalRef}>
        <div className="tpa-modal__header">
          <div className="tpa-modal__header__img">
            <img src={backgroundImage} width={145} />
          </div>
          <div className="button button--sq-md button--transparent" onClick={props.closeModal}>
            <Close />
          </div>
        </div>
        <div className="tpa-modal__body">
          <div className="t--xl t--500">Enroll A New Entity</div>
          <div className="t--subtitle">
            340B ESP enables users to enroll additional entities to their 340B ESP account. Only
            entities that are not yet part of a 340B ESP account may be newly enrolled.
          </div>
          <div>
            <div className="section__title">Select Your Entity</div>
            <AsyncSelect
              components={{ DropdownIndicator: null, IndicatorSeparator: null, ClearIndicator }}
              openMenuOnFocus={false}
              openMenuOnClick={false}
              name="entity_id"
              placeholder={"Search Covered Entity *"}
              styles={customSelectStyles}
              defaultOptions={false}
              isClearable={true}
              onChange={(event) => {
                if (event) {
                  setSelectedEntity({ id: event.value, id_340b: event.id_340b });
                } else {
                  setSelectedEntity();
                }
              }}
              loadOptions={(query) => debouncedLoadOptions(query)}
            />
          </div>
          <div className="flex flex-column gap--16">{renderCheckboxes()}</div>
          <div className="flex justify-content-end gap--16">
            <button className="button button--white button--lg t--500" onClick={props.closeModal}>
              Cancel
            </button>
            <button
              className="button button--blue button--lg t--500"
              disabled={submitDisabled()}
              onClick={handleSubmit}
            >
              Enroll
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreationModal;
